const CAMEL_BASE = '/api/camel'

export class CamelRequestError extends Error {
  readonly status: number
  readonly path: string
  readonly body: unknown
  readonly retryAfterSeconds: number | undefined

  constructor(status: number, path: string, body?: unknown, retryAfterSeconds?: number) {
    super(`camel request failed: ${status}`)
    this.name = 'CamelRequestError'
    this.status = status
    this.path = path
    this.body = body
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export const retryAfterSecondsOf = (header: string | null | undefined): number | undefined => {
  if (!header || !/^\d+$/.test(header.trim())) return undefined
  return Number(header.trim())
}

export type CamelParamValue = string | number
export type CamelParams = Record<string, CamelParamValue | CamelParamValue[] | undefined>

export type CamelDeleteOptions = { params?: CamelParams; body?: unknown }

export type CamelPostOptions = { keepalive?: boolean }

type CamelRequest = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  params?: CamelParams
  body?: unknown
  signal?: AbortSignal
  keepalive?: boolean
}

const queryString = (params: CamelParams | undefined): string => {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    const values = Array.isArray(value) ? value : [value]
    for (const item of values) search.append(key, String(item))
  }
  const rendered = search.toString()
  return rendered ? `?${rendered}` : ''
}

const parseBody = (text: string): unknown => (text === '' ? undefined : JSON.parse(text))

const parseErrorBody = (text: string): unknown => {
  try {
    return parseBody(text)
  } catch {
    return undefined
  }
}

export type CamelRequestFunctions = {
  get: (path: string, params?: CamelParams, signal?: AbortSignal) => Promise<unknown>
  post: (path: string, body?: unknown, signal?: AbortSignal, options?: CamelPostOptions) => Promise<unknown>
  put: (path: string, body?: unknown, signal?: AbortSignal) => Promise<unknown>
  patch: (path: string, body?: unknown, signal?: AbortSignal) => Promise<unknown>
  delete: (path: string, options?: CamelDeleteOptions, signal?: AbortSignal) => Promise<unknown>
}

export type CamelClientConfig = {
  baseUrl?: string
}

export const createCamelClient = ({ baseUrl = CAMEL_BASE }: CamelClientConfig = {}): CamelRequestFunctions => {
  const camelRequest = async ({ method, path, params, body, signal, keepalive }: CamelRequest): Promise<unknown> => {
    const init: RequestInit = { method, credentials: 'include', signal }
    if (keepalive) init.keepalive = true
    if (body !== undefined) {
      init.headers = { 'Content-Type': 'application/json' }
      init.body = JSON.stringify(body)
    }
    const response = await fetch(`${baseUrl}${path}${queryString(params)}`, init)
    const text = await response.text()
    if (!response.ok)
      throw new CamelRequestError(
        response.status,
        path,
        parseErrorBody(text),
        retryAfterSecondsOf(response.headers.get('Retry-After')),
      )
    return parseBody(text)
  }

  return {
    get: (path, params, signal) => camelRequest({ method: 'GET', path, params, signal }),
    post: (path, body, signal, options = {}) =>
      camelRequest({ method: 'POST', path, body, signal, keepalive: options.keepalive }),
    put: (path, body, signal) => camelRequest({ method: 'PUT', path, body, signal }),
    patch: (path, body, signal) => camelRequest({ method: 'PATCH', path, body, signal }),
    delete: (path, { params, body } = {}, signal) => camelRequest({ method: 'DELETE', path, params, body, signal }),
  }
}

const sameOriginCamelClient = createCamelClient()

export const camelGet = sameOriginCamelClient.get
export const camelPost = sameOriginCamelClient.post
export const camelPut = sameOriginCamelClient.put
export const camelPatch = sameOriginCamelClient.patch
export const camelDelete = sameOriginCamelClient.delete
